import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

/** localStorage wrappers that never throw (private mode, quota, blocked storage). */
export function readStored<T>(key: string, fallback: T, validate?: (v: unknown) => v is T): T {
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return fallback;
        const parsed: unknown = JSON.parse(raw);
        if (validate && !validate(parsed)) return fallback;
        return parsed as T;
    } catch {
        return fallback;
    }
}

export function writeStored(key: string, value: unknown): void {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* ignore */
    }
}

/** useState backed by localStorage (re-reads when `key` changes). */
export function usePersistentState<T>(
    key: string,
    fallback: T,
    validate: (v: unknown) => v is T
): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => readStored(key, fallback, validate));
    const [lastKey, setLastKey] = useState(key);
    if (lastKey !== key) {
        setLastKey(key);
        setValue(readStored(key, fallback, validate));
    }
    useEffect(() => {
        writeStored(key, value);
    }, [key, value]);
    return [value, setValue];
}

export const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";
